/* Generic reference-data manager used by many Phase 0 pages
   (Countries, Currencies, Languages, Timezones, Companies, Branches, etc.).
   Keeps every screen on the same shadcn pattern so the UI stays consistent. */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { RequirePermission } from "@/lib/rbac";

/** Relation dropdown — loads parent options from an endpoint and stores id (+ denormalized name). */
function SelectField({ field, value, onChange }) {
  const { data } = useQuery({
    queryKey: ["opts", field.optionsEndpoint],
    queryFn: async () => (await api.get(field.optionsEndpoint)).data,
    staleTime: 5 * 60_000,
  });
  const options = Array.isArray(data) ? data : (data?.items || []);
  const valKey = field.optionValue || "id";
  const labKey = field.optionLabel || "name";
  return (
    <select
      data-testid={`select-${field.key}`}
      className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
      value={value ?? ""}
      onChange={(e) => {
        const opt = options.find((o) => String(o[valKey]) === e.target.value);
        onChange(e.target.value, opt ? opt[labKey] : "");
      }}
    >
      <option value="">— Select {field.label} —</option>
      {options.map((o) => (
        <option key={o[valKey]} value={o[valKey]}>{o[labKey]}{o.code ? ` (${o.code})` : ""}</option>
      ))}
    </select>
  );
}

/**
 * props:
 *  - title, subtitle
 *  - endpoint  e.g. "/foundation/currencies"
 *  - queryKey
 *  - fields:   [{ key, label, type: "text"|"number"|"bool", wide?, defaultValue? }]
 *  - columns:  [{ key, label, render?(row) }]
 *  - canDelete: bool (default true)
 *  - readOnly:  bool (default false) — show list only
 *  - emptyHint: jsx
 *  - kpis:     optional [{label, value}] above the table
 */
export default function ResourcePage({
  title, subtitle, endpoint, queryKey, fields, columns,
  canDelete = true, readOnly = false, emptyHint, kpis, permModule = "FOUNDATION", headerActions,
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(() => defaults(fields));

  const { data, isLoading } = useQuery({
    queryKey: [queryKey, search],
    queryFn: async () => (await api.get(endpoint, { params: search ? { q: search } : {} })).data,
  });

  const upsert = useMutation({
    mutationFn: async () => {
      const missing = fields.filter((f) => f.required && !String(form[f.key] ?? "").trim());
      if (missing.length) {
        const err = new Error(`${missing[0].label} is required`);
        err._validation = true;
        throw err;
      }
      if (editing) {
        return (await api.patch(`${endpoint}/${editing.id}`, form)).data;
      }
      return (await api.post(endpoint, form)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); setEditing(null); setForm(defaults(fields));
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (e) => toast.error(e?._validation ? e.message : formatApiError(e)),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`${endpoint}/${id}`)).data,
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: [queryKey] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const openEdit = (row) => { setEditing(row); setForm({ ...defaults(fields), ...pick(row, fields.map((f) => f.key)) }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(defaults(fields)); setOpen(true); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Foundation</p>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Search" className="pl-9 h-9 w-56" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {headerActions}
          {!readOnly && (
            <RequirePermission perm={`${permModule}.CREATE`}>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button data-testid={`${queryKey}-add`} className="bg-primary hover:bg-primary/90" onClick={openNew}>
                  <Plus className="h-4 w-4 mr-2" /> New
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-display">{editing ? `Edit ${title}` : `New ${title}`}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                  {fields.map((f) => (
                    <div key={f.key} className={f.wide ? "col-span-2" : ""}>
                      <Label className="text-xs">{f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}</Label>
                      {f.type === "bool" ? (
                        <div className="mt-2"><Switch checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} /></div>
                      ) : f.type === "select" ? (
                        <SelectField field={f} value={form[f.key]} onChange={(val, name) => setForm({ ...form, [f.key]: val, ...(f.nameKey ? { [f.nameKey]: name } : {}) })} />
                      ) : f.type === "textarea" ? (
                        <textarea className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" rows={4} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                      ) : (
                        <Input
                          type={f.type === "number" ? "number" : "text"}
                          value={form[f.key] ?? ""}
                          onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? +e.target.value : e.target.value })}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button data-testid={`${queryKey}-save`} className="bg-primary hover:bg-primary/90" disabled={upsert.isPending} onClick={() => upsert.mutate()}>
                    {upsert.isPending ? "Saving…" : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </RequirePermission>
          )}
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpis.map((k, i) => (
            <div key={i} className="rounded-md border border-border bg-card p-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.label}</div>
              <div className="mt-2 font-display text-2xl font-semibold tracking-tight">{k.value}</div>
            </div>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="border-b border-border"><CardTitle className="text-base">Records · {data?.length || 0}</CardTitle></CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : (data?.length || 0) === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">{emptyHint || "No records yet."}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                  {!readOnly && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((c) => <TableCell key={c.key}>{c.render ? c.render(row) : row[c.key]}</TableCell>)}
                    {!readOnly && (
                      <TableCell className="text-right">
                        <RequirePermission anyOf={[`${permModule}.EDIT`, `${permModule}.DELETE`]} fallback={<span className="text-xs text-muted-foreground">—</span>}>
                          <RequirePermission perm={`${permModule}.EDIT`}>
                            <Button variant="ghost" size="icon" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>
                          </RequirePermission>
                          {canDelete && <RequirePermission perm={`${permModule}.DELETE`}>
                            <Button variant="ghost" size="icon" onClick={() => remove.mutate(row.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          </RequirePermission>}
                        </RequirePermission>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function defaults(fields) {
  const obj = {};
  for (const f of fields) {
    obj[f.key] = f.defaultValue ?? (f.type === "number" ? 0 : f.type === "bool" ? false : "");
  }
  return obj;
}

function pick(o, keys) {
  const out = {};
  for (const k of keys) out[k] = o[k];
  return out;
}
